'use client';
import cn from 'classnames';
import { ProductCard } from 'components/product-card';
import { SimpleImage } from 'components/simple-image';
import { Subscription } from 'components/subscription';
import {
  Column,
  Container,
  Row,
  Spacer,
  Typo,
  YouTubeVideo,
} from 'components/ui-kit';
import { useMedia } from 'hooks';
import { SubscriptionTag, TProductResponse, carrotService } from 'lib';
import Link from 'next/link';
import imageHeroDesk from 'public/images/collaborations/dalniy-vostok/hero_desk.png';
import imageHeroMob from 'public/images/collaborations/dalniy-vostok/hero_mob.png';
import { useCallback, useState } from 'react';
import styles from './collaboration-dalniy-vostok-page.module.scss';
import { Breakpoint } from '../../_shared';
import Modal from '../../components/ui-kit/modal/modal.component';

type TDalniyVostokPageProps = {
  products: Array<TProductResponse>;
};

export const DalniyVostokPage = ({
  products = [],
}: TDalniyVostokPageProps): JSX.Element => {
  const breakpoint = useMedia();
  const [isShowModal, setIsShowModal] = useState(false);

  const onSubscribeHandler = useCallback((email: string) => {
    return carrotService.subscribePromo(email, SubscriptionTag.SITE_FOOTER);
  }, []);

  return (
    <>
      <a href="#contest" className={styles.anchor}>
        <img
          src="/images/collaborations/dalniy-vostok/ancor.png"
          alt="olo"
          className={styles.anchorImage}
        />
      </a>

      <section className={cn(styles.hero, styles.section)}>
        <SimpleImage
          placeholder="blur"
          quality={90}
          objectFit="cover"
          objectPosition="top"
          layout="responsive"
          src={
            breakpoint <= Breakpoint.MEDIUM_SCREEN
              ? imageHeroMob
              : imageHeroDesk
          }
          className={styles.heroImage}
          alt="olo"
        />

        <div className={styles.heroTextWrapper}>
          <div className={styles.heroTitle}>
            <div>
              <h1 className={styles.heroHeader}>
                ТОЧКА &times;
                {breakpoint < Breakpoint.MEDIUM_SCREEN && <br />}
                OLA OLA &times; OKTAGON
              </h1>
              <div className={cn(styles.heroSubheader)}>
                Коллекция, вдохновленная Дальним Востоком и&nbsp;силой
                предпринимательства
              </div>
            </div>
          </div>
          <Container className={styles.heroDescription}>
            <a
              href="#products"
              className={cn(styles.moreButton, styles.moreButtonAccent)}
            >
              Перейти к&nbsp;товарам
            </a>
            <a
              href="#contest"
              className={cn(styles.moreButton, styles.moreButtonAccent)}
            >
              Итоги конкурса
            </a>
          </Container>
        </div>
      </section>

      {breakpoint > Breakpoint.SMALL_SCREEN && (
        <>
          <Container>
            <Typo
              align="center"
              type="h2"
              weight="500"
              className={styles.sectionHeader}
            >
              Мы&nbsp;уверены, что путь предпринимателя&nbsp;&mdash; это
              {breakpoint < Breakpoint.LARGE_SCREEN ? <br /> : ' '}
              путь первопроходца и&nbsp;путешественника
            </Typo>

            <div className={styles.travelGalleryContainer}>
              <img
                alt=""
                src="/images/collaborations/dalniy-vostok/firststep.png"
                className={styles.travelGalleryImage}
              />
            </div>
          </Container>
          <Spacer size={100} />
        </>
      )}

      <Container className={styles.section}>
        <Typo type="div" align="center" className={styles.sectionText}>
          Ловить попутный ветер на&nbsp;дороге, полной опасностей
          и&nbsp;приключений. Пробираться сквозь дикие и&nbsp;нехоженные места.
          И&nbsp;верить, что за&nbsp;чередой сложностей откроются лучшие виды.
          Ведь оно того стоит, не&nbsp;так&nbsp;ли?
        </Typo>
      </Container>

      <Container className={styles.section}>
        <Typo type="h2" align="center" className={styles.sectionHeader}>
          Воля и&nbsp;смелость, чтобы стать первопроходцами, есть в&nbsp;каждом.
        </Typo>
        {breakpoint < Breakpoint.MEDIUM_SCREEN && (
          <>
            <Typo align="center" className={styles.text}>
              Эти качества есть в&nbsp;достатке у&nbsp;ребят, с&nbsp;которыми
              мы&nbsp;создали новую коллекцию.
            </Typo>
            <Spacer size={24} />
          </>
        )}
        <Row>
          <Column span={{ s: 12, m: 6 }}>
            <img
              src="/images/collaborations/dalniy-vostok/oktagon.png"
              alt=""
              className={cn(styles.image, styles.personImage)}
            />
            <Typo className={styles.smallerText}>
              Дмитрий Куликов, известный как Oktagon,&nbsp;&mdash;
              предприниматель и&nbsp;путешественник, который проложил авторские
              маршруты через заповедные места Дальнего Востока.
            </Typo>
          </Column>
          <Column span={{ s: 12, m: 6 }}>
            {breakpoint > Breakpoint.MEDIUM_SCREEN && (
              <Typo className={styles.text}>
                Эти качества есть в&nbsp;достатке у&nbsp;ребят, с&nbsp;которыми
                мы&nbsp;создали новую коллекцию.
              </Typo>
            )}
            <Spacer size={breakpoint > Breakpoint.MEDIUM_SCREEN ? 100 : 0} />
            <img
              src="/images/collaborations/dalniy-vostok/ola.png"
              alt=""
              className={cn(styles.image, styles.personImage)}
            />

            <Typo className={styles.smallerText}>
              Ольга Гинзбург&nbsp;&mdash; создательница бренда одежды
              и&nbsp;аксессуаров OLA OLA, которая потрясающе работает
              со&nbsp;сложными принтами и&nbsp;фотографией.
            </Typo>
          </Column>
        </Row>
      </Container>

      <Container className={styles.section}>
        <Typo align="center" className={cn(styles.sectionText)}>
          А&nbsp;ещё воля и&nbsp;смелость&nbsp;&mdash; это про Дальний Восток,
          которому мы&nbsp;посвятили коллекцию. Он&nbsp;строптивый, уникальный
          и&nbsp;полный открытий. Как путь каждого предпринимателя.
        </Typo>
      </Container>

      <section className={cn(styles.section, styles.promoSection)}>
        <Container>
          <Typo className={styles.sectionHeader}>
            Новая коллекция&nbsp;&mdash; путеводная нить в&nbsp;путешествие{' '}
            {breakpoint > Breakpoint.MEDIUM_SCREEN ? ' ' : <br />}
            на&nbsp;Дальний Восток
          </Typo>
        </Container>

        <Container className={styles.promoContainer}>
          <div className={styles.promoRow}>
            <Link href="/product/tolstovka-ohotskoe-more/">
              <img
                src="/images/collaborations/dalniy-vostok/product_1.png"
                alt="Толстовка «Охотское море»"
                className={styles.image}
              />
            </Link>
            <Link href="/product/noski-belye-skaly-haki/">
              <img
                src="/images/collaborations/dalniy-vostok/products/product-socks.png"
                alt="Носки «Белые скалы», хаки"
                className={styles.image}
              />
            </Link>
            <Link href="/product/futbolka-avachinskaya-sopka/">
              <img
                src="/images/collaborations/dalniy-vostok/product_3.png"
                alt="Футболка «Авачинская сопка»"
                className={styles.image}
              />
            </Link>
          </div>
          {breakpoint >= Breakpoint.MEDIUM_SCREEN && (
            <>
              <Spacer size={80} />
              <a
                href="#products"
                className={cn(styles.moreButton, styles.center)}
              >
                Перейти к&nbsp;товарам
              </a>
            </>
          )}
        </Container>
      </section>

      {breakpoint < Breakpoint.MEDIUM_SCREEN ? (
        <>
          <Spacer size={20} />
          <Container>
            <a
              href="#products"
              className={cn(styles.moreButton, styles.center)}
            >
              Перейти к&nbsp;товарам
            </a>
          </Container>
          <Spacer size={40} />
        </>
      ) : (
        <Spacer size={64} />
      )}

      <Container className={styles.section}>
        <Typo align="center" className={cn(styles.sectionText)}>
          В&nbsp;каждом принте на&nbsp;вещах&nbsp;&mdash; образы Дальнего
          Востока, которые Дмитрий Куликов запечатлел на&nbsp;камеру,
          <br />
          а&nbsp;Ольга Гинзбург превратила в&nbsp;иллюстрации. На&nbsp;бирках
          мы&nbsp;указали координаты места, вдохновившего на&nbsp;узор,
          и&nbsp;рассказали его историю.
        </Typo>
      </Container>

      <Container className={styles.section}>
        <img
          src="/images/collaborations/dalniy-vostok/traveler.png"
          alt=""
          className={styles.image}
        />
        <div className={styles.olaBottomGallery}>
          <div className={styles.olaBottomGalleryColumn}>
            <img
              src="/images/collaborations/dalniy-vostok/vostok3.png"
              alt=""
              className={styles.image}
            />
          </div>
          <div className={styles.olaBottomGalleryColumn}>
            <img
              src="/images/collaborations/dalniy-vostok/vostok4.png"
              alt=""
              className={styles.image}
            />
          </div>
        </div>
      </Container>

      <article
        id="contest"
        className={cn(styles.contrastBg, styles.innerOffset, styles.section)}
      >
        <section>
          <Typo
            type="h2"
            className={cn(styles.sectionHeader, styles.contrastText)}
          >
            ИТОГИ КОНКУРСА
          </Typo>
          <Container className={styles.contestGallery}>
            <div className={styles.contestGalleryRow}>
              <img
                src="/images/collaborations/dalniy-vostok/contest.png"
                alt=""
                className={styles.image}
              />
            </div>
          </Container>
          <Container>
            <Row>
              <Column span={{ s: 12, m: 6 }}>
                <Typo
                  className={cn(styles.contestIntroText, styles.contrastText)}
                >
                  Поздравляем Оксану Брозовскую!
                  <br />
                  <br />
                  Оксана отправляется в&nbsp;путешествие на&nbsp;Сахалин
                  и&nbsp;Итуруп на&nbsp;двоих на&nbsp;8&nbsp;дней
                  в&nbsp;компании Oktagon.Travel.
                </Typo>
              </Column>
              <Column
                span={{ s: 12, m: 6 }}
                className={styles.contestWinnerDescription}
              >
                <Typo
                  className={cn(styles.contestIntroText, styles.contrastText)}
                >
                  Смотреть{' '}
                  <span
                    onClick={() => setIsShowModal(true)}
                    className={styles.link}
                  >
                    видео
                  </span>{' '}
                  подведения итогов
                </Typo>
                <div className={styles.contestWinnerLinkBlock}>
                  <a
                    target="_blank"
                    href="/images/collaborations/dalniy-vostok/regulation.pdf"
                    className={cn(styles.contestLink, styles.contrastText)}
                  >
                    Положение о&nbsp;конкурсе.pdf
                  </a>
                  <br />
                  <a
                    target="_blank"
                    href="/images/collaborations/dalniy-vostok/memo.pdf"
                    className={cn(styles.contestLink, styles.contrastText)}
                  >
                    Памятка для тех, кто собирается на&nbsp;Сахалин.pdf
                  </a>
                </div>
              </Column>
            </Row>
          </Container>
          <Spacer size={64} />
          <div className={styles.contestImages}>
            <img
              src="/images/collaborations/dalniy-vostok/tour1.png"
              alt=""
              className="collab-ola__gallery-img collab-ola__gallery-img--pos-normal collab-ola__tour-gallery-img"
            />
            <img
              src="/images/collaborations/dalniy-vostok/tour2.png"
              alt=""
              className="collab-ola__gallery-img collab-ola__gallery-img--pos-normal collab-ola__tour-gallery-img"
            />
          </div>
        </section>
        <div id="products" />
      </article>
      <Container className={styles.section}>
        <Typo className={styles.sectionHeader}>
          добавьте в&nbsp;гардероб сопки, море и&nbsp;скалы
        </Typo>
        <Row className="catalog-products__list collab-ola__products-list">
          {products.map((product: TProductResponse) => (
            <Column key={product.id} span={{ s: 4 }}>
              <Link
                className={styles.clearLink}
                href={`/product/${product.slug}`}
              >
                <ProductCard product={product} />
              </Link>
            </Column>
          ))}
        </Row>
      </Container>

      <Container>
        <Subscription onSubscribe={onSubscribeHandler} />
      </Container>

      <Modal
        portalClassName={styles.modalWrapper}
        isOpen={isShowModal}
        onRequestClose={() => setIsShowModal(false)}
        className={styles.modal}
      >
        {/* <Typo align="center" weight="700">*/}
        {/*  Итоги конкурса*/}
        {/* </Typo>*/}
        <div className={styles.modalContent}>
          <YouTubeVideo
            buttonImg="/images/collaborations/beat/play.png"
            link="https://www.youtube.com/embed/EPJyHIpRxak"
            title="Кто выиграл поездку на Сахалин и Итуруп? Универмаг от Точки подводит итоги конкурса"
            posterImg="/images/collaborations/dalniy-vostok/bg_home_ola.jpg"
            altForPosterImg="Итоги конкурса"
          />
        </div>
      </Modal>
    </>
  );
};
